'use client';

import type { SectionBlock, PageBlock } from '@impact/types';
import { cn } from '@/lib/utils';
import type { EditableWrapper } from './types';
import { BlockRenderer } from './BlockRenderer';
import { SectionGradientMesh } from './section-variants/GradientMesh';
import { SectionVideoBackground } from './section-variants/VideoBackground';
import { SectionDiagonalMask } from './section-variants/DiagonalMask';
import { AddNestedBlockButton } from './_editing/AddNestedBlockButton';

interface SectionBlockRendererProps {
  block: SectionBlock;
  editing?: boolean;
  Wrapper?: EditableWrapper;
}

const backgroundClasses: Record<SectionBlock['data']['background'], string> = {
  none: '',
  light: 'bg-background',
  dark: 'bg-zinc-900',
  muted: 'bg-muted/50',
  primary: 'bg-primary',
  'primary-subtle': 'bg-primary/10',
};

const contentWidthClasses: Record<SectionBlock['data']['contentWidth'], string> = {
  narrow: 'max-w-3xl',
  default: 'max-w-4xl',
  wide: 'max-w-6xl',
  full: 'max-w-7xl',
};

const paddingClasses: Record<SectionBlock['data']['padding'], string> = {
  none: 'py-0',
  sm: 'py-4',
  md: 'py-8',
  lg: 'py-12 md:py-16',
  xl: 'py-16 md:py-24',
};

function getTextColorClass(
  textColor: SectionBlock['data']['textColor'],
  background: SectionBlock['data']['background'],
): string {
  if (textColor === 'light')
    return 'text-white [&_*]:text-white [&_.text-muted-foreground]:text-white/70';
  if (textColor === 'dark') return 'text-foreground';
  // Auto-detect for dark/primary backgrounds when no explicit textColor
  if (background === 'dark' || background === 'primary')
    return 'text-white [&_*]:text-white [&_.text-muted-foreground]:text-white/70';
  return '';
}

export function SectionBlockRenderer({ block, editing, Wrapper }: SectionBlockRendererProps) {
  switch (block.data.variant ?? 'default') {
    case 'gradient-mesh':
      return <SectionGradientMesh block={block} editing={editing} Wrapper={Wrapper} />;
    case 'video-bg':
      return <SectionVideoBackground block={block} editing={editing} Wrapper={Wrapper} />;
    case 'diagonal-mask':
      return <SectionDiagonalMask block={block} editing={editing} Wrapper={Wrapper} />;
    default:
      break;
  }

  const { blocks, background, backgroundColor, backgroundWidth, contentWidth, textColor, padding } =
    block.data;

  const hasCustomColor = Boolean(backgroundColor);
  const bgClass = hasCustomColor ? '' : backgroundClasses[background];
  const widthClass = contentWidthClasses[contentWidth];
  const padClass = paddingClasses[padding];
  const txtClass = getTextColorClass(textColor, background);
  const bgInlineStyle = hasCustomColor ? { backgroundColor } : undefined;

  const renderedBlocks = blocks.map((child: PageBlock) => (
    <BlockRenderer key={child.id} block={child} editing={editing} Wrapper={Wrapper} />
  ));

  const isEmpty = !blocks || blocks.length === 0;
  const editorAddButton = editing ? (
    <div className={cn('flex w-full', isEmpty ? 'justify-center py-6' : 'justify-center pt-4')}>
      <AddNestedBlockButton
        blockId={block.id}
        arrayPath="data.blocks"
        variant={isEmpty ? 'placeholder' : 'inline'}
        label={isEmpty ? 'Add a block to this section' : 'Add block'}
      />
    </div>
  ) : null;

  if (backgroundWidth === 'container') {
    return (
      <div className={padClass}>
        <div className="container">
          <div
            style={bgInlineStyle}
            className={cn(
              widthClass,
              'mx-auto rounded-xl space-y-8',
              bgClass,
              txtClass,
              (background !== 'none' || hasCustomColor) && 'p-8',
              editing && isEmpty && 'border-2 border-dashed border-muted-foreground/20',
            )}
          >
            {blocks.length > 0 && renderedBlocks}
            {editorAddButton}
          </div>
        </div>
      </div>
    );
  }

  return (
    <section
      style={bgInlineStyle}
      className={cn(
        bgClass,
        padClass,
        txtClass,
        editing && isEmpty && 'border-2 border-dashed border-muted-foreground/20',
      )}
    >
      <div className="container">
        <div className={cn(widthClass, 'mx-auto space-y-8')}>
          {blocks.length > 0 && renderedBlocks}
          {editorAddButton}
        </div>
      </div>
    </section>
  );
}
